import { Component, OnInit, OnDestroy, ViewChild } from '@angular/core';
import { ChartComponent } from 'ng-apexcharts';
import { Router } from '@angular/router';
import { Subscription } from 'rxjs';
import {
  ApexAxisChartSeries,
  ApexChart,
  ApexXAxis,
  ApexDataLabels,
  ApexTooltip,
  ApexStroke,
  ApexYAxis,
  ApexGrid,
  ApexPlotOptions,
  ApexFill,
  ApexResponsive,
  ApexTitleSubtitle,
  ApexLegend,
  ApexNonAxisChartSeries
} from 'ng-apexcharts';

import { DashboardService, DashboardMetrics, ProcessTimeline, ProcessStatus, UserActivity } from '../../services/dashboard.service';
import { BaseServiceService } from '../../services/base-service.service';

export interface ChartOptions {
  series: ApexAxisChartSeries | ApexNonAxisChartSeries;
  chart: ApexChart;
  xaxis?: ApexXAxis;
  yaxis?: ApexYAxis;
  dataLabels?: ApexDataLabels;
  grid?: ApexGrid;
  stroke?: ApexStroke;
  title?: ApexTitleSubtitle;
  colors?: string[];
  fill?: ApexFill;
  plotOptions?: ApexPlotOptions;
  legend?: ApexLegend;
  tooltip?: ApexTooltip;
  responsive?: ApexResponsive[];
  labels?: string[];
}

@Component({
  selector: 'app-dashboard',
  templateUrl: './dashboard.component.html',
  styleUrls: ['./dashboard.component.css']
})
export class DashboardComponent implements OnInit, OnDestroy {
  @ViewChild('chart') chart: ChartComponent;

  // Opciones para las gráficas
  public timelineChartOptions: Partial<ChartOptions>;
  public processStatusChartOptions: Partial<ChartOptions>;
  public usersActivityChartOptions: Partial<ChartOptions>;

  private subscriptions: Subscription[] = [];

  // Rutas permitidas según permisos del usuario
  private allowedRoutes: Set<string> = new Set<string>();

  // Datos del dashboard
  dashboardData: DashboardMetrics = {
    TotalProcesses: 0,
    ActiveTasks: 0,
    CompletedProcesses: 0,
    CancelledProcesses: 0,
    PendingTasks: 0,
    ActiveUsers: 0,
    ExecutionProcesses: 0,
    AverageExecutionTime: 0
  };

  // Accesos directos eliminados - ya no necesarios para la cuadrícula estática

  constructor(
    private router: Router, 
    private dashboardService: DashboardService,
    private baseService: BaseServiceService
  ) {
    this.initializeCharts();
  }

  ngOnInit(): void {
    this.initializeCharts();
    this.loadDashboardData();
    const permSub = this.baseService.currentUserPermissions.subscribe((permissions) => {
      if (permissions) {
        const items = JSON.parse(permissions);
        this.allowedRoutes = new Set<string>();
        this.collectRoutes(items, this.allowedRoutes);
      }
    });
    this.subscriptions.push(permSub);
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach(sub => sub.unsubscribe());
  }

  private initializeCharts(): void {
    // Gráfico de línea de tiempo de ejecución de procesos
    this.timelineChartOptions = {
      series: [{
        name: 'Tiempo de Ejecución (horas)',
        data: [] // Sin datos iniciales, se cargarán del backend
      }],
      chart: {
        height: 280,
        type: 'line',
        toolbar: {
          show: false
        },
        animations: {
          enabled: true,
          easing: 'easeinout',
          speed: 800
        }
      },
      stroke: {
        curve: 'smooth',
        width: 3
      },
      colors: ['#0078d4'],
      title: {
        text: 'Tiempos de Ejecución de Procesos',
        style: {
          fontSize: '16px',
          fontWeight: 600,
          color: '#323130'
        }
      },
      grid: {
        borderColor: '#e1dfdd',
        strokeDashArray: 4
      },
      xaxis: {
        categories: [], // Sin categorías iniciales, se cargarán del backend
        labels: {
          style: {
            colors: '#605e5c',
            fontSize: '12px'
          }
        }
      },
      yaxis: {
        title: {
          text: 'Horas',
          style: {
            color: '#605e5c',
            fontSize: '12px'
          }
        },
        labels: {
          style: {
            colors: '#605e5c',
            fontSize: '12px'
          }
        }
      },
      dataLabels: {
        enabled: false
      },
      tooltip: {
        theme: 'light'
      }
    };

    // Gráfico de dona para estado de procesos
    this.processStatusChartOptions = {
      series: [],
      chart: {
        height: 300,
        type: 'donut',
        animations: {
          enabled: true,
          easing: 'easeinout',
          speed: 800
        }
      },
      colors: ['#ff8c00', '#0078d4', '#107c10', '#d13438', '#605e5c'],
      labels: [],
      title: {
        text: 'Estado de Procesos',
        style: {
          fontSize: '16px',
          fontWeight: 600,
          color: '#323130'
        }
      },
      legend: {
        position: 'bottom',
        labels: {
          colors: '#605e5c'
        }
      },
      dataLabels: {
        enabled: true,
        style: {
          colors: ['#fff']
        }
      },
      tooltip: {
        theme: 'light'
      },
      responsive: [{
        breakpoint: 480,
        options: {
          chart: {
            width: 200
          },
          legend: {
            position: 'bottom'
          }
        }
      }]
    };

    // Gráfico de área para actividad de usuarios
    this.usersActivityChartOptions = {
      series: [{
        name: 'Usuarios Activos',
        data: []
      }],
      chart: {
        height: 280,
        type: 'area',
        toolbar: {
          show: false
        },
        animations: {
          enabled: true,
          easing: 'easeinout',
          speed: 800
        }
      },
      fill: {
        type: 'gradient',
        gradient: {
          shadeIntensity: 1,
          opacityFrom: 0.7,
          opacityTo: 0.3,
          stops: [0, 90, 100]
        }
      },
      colors: ['#e3008c'],
      title: {
        text: 'Actividad de Usuarios',
        style: {
          fontSize: '16px',
          fontWeight: 600,
          color: '#323130'
        }
      },
      stroke: {
        curve: 'smooth',
        width: 2
      },
      grid: {
        borderColor: '#e1dfdd',
        strokeDashArray: 4
      },
      xaxis: {
        categories: [],
        labels: {
          style: {
            colors: '#605e5c',
            fontSize: '12px'
          }
        }
      },
      yaxis: {
        title: {
          text: 'Usuarios',
          style: {
            color: '#605e5c',
            fontSize: '12px'
          }
        },
        labels: {
          style: {
            colors: '#605e5c',
            fontSize: '12px'
          }
        }
      },
      dataLabels: {
        enabled: false
      },
      tooltip: {
        theme: 'light'
      }
    };
  }

  private collectRoutes(items: any[], routes: Set<string>): void {
    for (const item of items) {
      if (item.routeLink && item.routeLink !== '#') {
        routes.add(item.routeLink);
      }
      if (item.items && item.items.length > 0) {
        this.collectRoutes(item.items, routes);
      }
    }
  }

  hasRoutePermission(route: string): boolean {
    return this.allowedRoutes.has(route);
  }

  hasAnyQuickAccess(): boolean {
    return ['procesos', 'formularios', 'usuarios', 'tareas'].some(r => this.allowedRoutes.has(r));
  }

  navigateTo(route: string): void {
    this.router.navigateByUrl(route);
  }

  // Método para obtener el porcentaje de progreso
  getProgressPercentage(current: number, total: number): number {
    return Math.round((current / total) * 100);
  }

  // Método para formatear números grandes
  formatNumber(num: number): string {
    if (num >= 1000) {
      return (num / 1000).toFixed(1) + 'k';
    }
    return num.toString();
  }

  // Cargar datos del dashboard
  private loadDashboardData(): void {

    // Cargar métricas principales
    const metricsSubscription = this.dashboardService.getDashboardMetrics().subscribe({
      next: (metrics) => {
        this.dashboardData = metrics;
      },
      error: (error) => {
        console.error('Error loading dashboard metrics:', error);
      }
    });
    this.subscriptions.push(metricsSubscription);

    // Cargar datos de timeline
    const timelineSubscription = this.dashboardService.getProcessTimeline().subscribe({
      next: (timeline) => {
        this.updateTimelineChart(timeline);
      },
      error: (error) => {
        console.error('Error loading timeline data:', error);
      }
    });
    this.subscriptions.push(timelineSubscription);

    // Cargar datos de estado de procesos
    const processStatusSubscription = this.dashboardService.getProcessStatus().subscribe({
      next: (processStatus) => {
        this.updateProcessStatusChart(processStatus);
      },
      error: (error) => {
        console.error('Error loading process status data:', error);
      }
    });
    this.subscriptions.push(processStatusSubscription);

    // Cargar datos de actividad de usuarios
    const userActivitySubscription = this.dashboardService.getUserActivity().subscribe({
      next: (userActivity) => {
        this.updateUsersChart(userActivity);
      },
      error: (error) => {
        console.error('Error loading user activity data:', error);
      }
    });
    this.subscriptions.push(userActivitySubscription);
  }

  // Actualizar gráfico de timeline
  private updateTimelineChart(timeline: any[]): void {
    
    if (!timeline || timeline.length === 0) {
      console.warn('No timeline data available');
      return;
    }

    this.timelineChartOptions = {
      ...this.timelineChartOptions,
      series: [{
        name: 'Tiempo de Ejecución (horas)',
        data: timeline.map(item => item.ExecutionTime)
      }],
      xaxis: {
        ...this.timelineChartOptions.xaxis,
        categories: timeline.map(item => item.Month)
      }
    };
  }

  // Actualizar gráfico de estado de procesos
  private updateProcessStatusChart(processStatus?: ProcessStatus[]): void {
    if (processStatus && processStatus.length > 0) {
      // Mapa de colores para cada estado
      const statusColors: { [key: string]: string } = {
        'Completados': '#28a745',     // Verde vibrante para completados
        'En Ejecución': '#007bff',    // Azul brillante para en ejecución
        'Cancelados': '#dc3545'       // Rojo vibrante para cancelados
      };

      // Usar datos reales del backend con colores del frontend
      this.processStatusChartOptions = {
        ...this.processStatusChartOptions,
        series: processStatus.map(status => status.Count),
        labels: processStatus.map(status => status.Status),
        colors: processStatus.map(status => statusColors[status.Status] || '#6c757d')
      };
    } else {
      // Mantener gráfico vacío si no hay datos
      this.processStatusChartOptions = {
        ...this.processStatusChartOptions,
        series: [],
        labels: []
      };
    }
  }

  // Actualizar gráfico de usuarios
  private updateUsersChart(users: UserActivity[]): void {
    
    if (users && users.length > 0) {
      this.usersActivityChartOptions = {
        ...this.usersActivityChartOptions,
        series: [{
          name: 'Usuarios Activos',
          data: users.map(user => user.ActiveUsers)
        }],
        xaxis: {
          ...this.usersActivityChartOptions.xaxis,
          categories: users.map(user => user.Month)
        }
      };
    } else {
      // Mantener gráfico vacío si no hay datos
      this.usersActivityChartOptions = {
        ...this.usersActivityChartOptions,
        series: [{
          name: 'Usuarios Activos',
          data: []
        }],
        xaxis: {
          ...this.usersActivityChartOptions.xaxis,
          categories: []
        }
      };
    }
  }
}
